/**
 * Whether the purchase carries a discount code the buyer applied.
 *
 * Keyed on the association, not on `discountAmount`. The amount looks like the obvious signal and is
 * not one: `PurchaseDetailSerializer#discount_amount` only reports the purchase-level coupon for
 * tenants without `SUM_REAL_COUPON_DISCOUNTS_FOR_TICKETS`. For the tenants that do set it — `gfa`
 * and `ttur` in the production configs, both with `DISCOUNT_CODE_ENABLED` — it is instead summed
 * across the tickets as `price + taxes - service_total`.
 *
 * That sum goes NEGATIVE after any re-lock. `Ticket#reset_lock_fields` zeroes `price` and `taxes`
 * while leaving `applied_coupon` and the discounted `service_total` untouched, so the surviving
 * coupon reads as `0 + 0 - service_total`. It only zeroes them when `transporter_key` is blank,
 * which for `gfa` is always: its adapter in `saas-integrations` implements no `createPassengers`,
 * and that call is what would set the key. A seat or insurance edit is enough to trigger it —
 * `updateQuickPurchaseInsurance` calls `updateQuickPurchaseSeats`.
 *
 * Reading the amount therefore turned this flag OFF while a coupon was still applied, which turned
 * off every confirmation it gates (`QuickCheckout.js`) exactly where they were needed: the buyer
 * then lost the discount to a passenger edit, or paid the undiscounted total, with no prompt at all.
 *
 * The association survives the re-lock — nothing in that path calls `Purchase#remove_discount!` —
 * and `Api::DiscountSerializer` exposes its `id`. `PurchasePricingTrip.js` already keys on the same
 * field for the same reason.
 *
 * @param {object} [purchase] - Purchase state.
 * @param {object} [purchase.discount] - The applied discount, when the purchase carries one.
 * @returns {boolean} True when a discount code is currently applied.
 */
export default function hasAppliedDiscount(purchase) {
  return Boolean(purchase?.discount?.id);
}
